import React from 'react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { DockGlyph } from '@/components/toolbar/DockGlyph';

// The formatting rails (text, arrows) look like the other floating canvas
// controls (see CanvasControl): round 40px buttons in a frosted pill, a soft
// fill on hover, a dark fill when on, a small press.

interface RailButtonProps {
  icon: React.ElementType;
  label: string;
  shortcut?: string;
  isActive?: boolean;
  onClick: () => void;
}

export function RailButton({ icon: Icon, label, shortcut, isActive, onClick }: RailButtonProps) {
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          aria-pressed={isActive}
          onClick={onClick}
          onMouseDown={(e) => e.preventDefault()} // Keep focus (and the selection) in the editor
          data-dock-item
          className={cn(
            'flex h-10 w-10 items-center justify-center rounded-full',
            'transition-[background-color,color,transform] duration-150 ease-out active:scale-95 motion-reduce:transition-none',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            isActive ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'text-foreground hover:bg-muted/60',
          )}
        >
          <DockGlyph>
            <Icon className="h-5 w-5" />
          </DockGlyph>
        </button>
      </TooltipTrigger>
      <TooltipContent side="right" className="font-medium">
        <div className="flex flex-col">
          <span>{label}</span>
          {shortcut && <span className="text-xs text-muted-foreground">{shortcut}</span>}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

/** A hairline between groups of rail buttons. */
export function RailDivider() {
  return <div className="my-1 h-px w-6 bg-line" />;
}

/** The frosted pill a rail sits in: the same surface and radius as the nav pills. */
export const RAIL_SURFACE = 'floating-pill rounded-full p-1';
